import React from 'react';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import AdminContainer from '../../Utility/AdminContainer';
import AdminBox from '../../Utility/AdminBox';
import AdminViewHeader from '../../Utility/AdminViewHeader';
import AdminViewContent from '../../Utility/AdminViewContent';
import useKnowledgebases from '../../../Hooks/useKnowledgebases';
import useGlobals from '../../../Hooks/useGlobals';
import { Button } from '../../Utility/Button';
import { PaginationWrapper } from '../../Utility/PaginationWrapper';
import { PaginationListType } from '../../../Types/PaginationParams';
import { Knowledgebase } from '../../../Types';
import { knowledgebaseService } from '../../../Services/knowledgebaseService';
import useSearchFilter from '../../../Hooks/useSearchFilter';
import SearchFilter from '../../Utility/SearchFilter';

const Knowledgebases: React.FC = () => {
    const { settings, userSettings, demo } = useGlobals();
    const { KnowledgebasesActions, handleNavigate, i18n, windowWidth, getBreakpoint } = useKnowledgebases();

    const { query, localQuery, searchActions } = useSearchFilter({
        initialQuery: null,
    });

    const GRID_COLS = 'grid-cols-[minmax(157px,300px),minmax(157px,200px),minmax(70px,70px),minmax(157px,1fr)]';

    return (
        <div className="admin-container flex relative flex-col">
            <AdminContainer>
                <AdminBox classString="px-4">
                    <SearchFilter
                        value={localQuery}
                        onChange={searchActions.setLocalQuery}
                        onApply={searchActions.applyQuery}
                        onClear={searchActions.clearQuery}
                    />
                    <div className="mb-4 md:hidden w-full">
                        {(!settings.demo || userSettings.isSuperAdmin) && (
                            <Button className="w-full mb-2" onClick={() => handleNavigate()}>
                                {i18n.translate({ id: 'button.new', sourceName: 'Knowledgebases' })}
                            </Button>
                        )}
                    </div>
                    <div className="hidden md:block">
                        <AdminViewHeader
                            gridCols={GRID_COLS}
                            cols={{
                                cols: [
                                    {
                                        text: i18n.translate({ id: 'adminHeader.mainCol', sourceName: 'Main' }),
                                    },
                                    {
                                        text: i18n.translate({
                                            id: 'list.documentCount',
                                            sourceName: 'Knowledgebases',
                                        }),
                                    },
                                    {
                                        className: 'flex items-center justify-center w-full',
                                        text: i18n.translate({ id: 'list.status', sourceName: 'Knowledgebases' }),
                                    },
                                ],
                                button:
                                    !settings.demo || userSettings.isSuperAdmin
                                        ? {
                                              text: i18n.translate({ id: 'button.new', sourceName: 'Knowledgebases' }),
                                              function: () => handleNavigate(),
                                          }
                                        : undefined,
                            }}
                        />
                        <hr className="border-layout-borderColor mt-5" />
                    </div>
                    <PaginationWrapper
                        listType={PaginationListType.Knowledgebases}
                        itemsFunction={knowledgebaseService.getKnowledgebasesByPage}
                        itemsPerPage={15}
                        query={query ?? null}
                        renderItem={(kb: Knowledgebase, index: number) => (
                            <AdminViewContent
                                key={kb.identifier}
                                mobile={windowWidth < getBreakpoint('md')}
                                identifier={kb.identifier}
                                gridCols={GRID_COLS}
                                cols={{
                                    optionalCols: [
                                        {
                                            content: kb.name,
                                        },
                                        {
                                            content: `${kb.documentCount} ${i18n.translate({
                                                id: 'list.documentCount',
                                                sourceName: 'Knowledgebases',
                                            })}`,
                                        },
                                        {
                                            className: 'flex items-center justify-center w-full',
                                            content: (
                                                <div
                                                    className={`w-3 h-3 rounded-full ${
                                                        kb.status === 'in_progress'
                                                            ? 'bg-orange-400'
                                                            : kb.status === 'error'
                                                            ? 'bg-red-500'
                                                            : 'bg-green-500'
                                                    }`}
                                                    title={i18n.translate({
                                                        id: 'status.' + kb.status,
                                                        sourceName: 'Knowledgebases',
                                                    })}
                                                />
                                            ),
                                        },
                                    ],
                                    button: {
                                        buttons: [
                                            {
                                                variant: 'primaryRound',
                                                onClick: () => handleNavigate(kb.identifier),
                                                icon: <PencilSimple />,
                                            },
                                            {
                                                variant: 'secondaryRound',
                                                onClick: () =>
                                                    KnowledgebasesActions.handleOpenConfirmDialog(kb.identifier),
                                                icon: <Trash />,
                                            },
                                        ],
                                    },
                                }}
                            />
                        )}
                    />
                </AdminBox>
            </AdminContainer>
        </div>
    );
};

export default Knowledgebases;
